import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface PageMetaProps {
  title: string
  description: string
  canonicalPath?: string
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  noindex?: boolean
  faviconVariant?: 'maia' | 'bar' | 'pizza'
}

function setOrUpdateMeta(property: string, content: string, attribute: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement | null
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, property)
    document.head.appendChild(element)
  }
  element.content = content
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = href
}

function setFaviconLink(rel: string, href: string, attrs?: Record<string, string>) {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
  if (attrs) {
    Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value))
  }
}

export function PageMeta({
  title,
  description,
  canonicalPath,
  jsonLd,
  noindex,
  faviconVariant = 'maia',
}: PageMetaProps) {
  const { i18n } = useTranslation()
  const lang = i18n.language
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://maiaetas.it'
  const canonical = canonicalPath ? `${siteUrl}${canonicalPath}` : `${siteUrl}/${lang}`

  useEffect(() => {
    document.title = title

    setOrUpdateMeta('description', description)
    setOrUpdateMeta('og:title', title, 'property')
    setOrUpdateMeta('og:description', description, 'property')
    setOrUpdateMeta('og:type', 'website', 'property')
    setOrUpdateMeta('og:locale', lang === 'it' ? 'it_IT' : 'en_US', 'property')
    setOrUpdateMeta('og:url', canonical, 'property')
    setOrUpdateMeta('og:image', `${siteUrl}/images/link-image-social.jpg`, 'property')
    setOrUpdateMeta('og:image:width', '1200', 'property')
    setOrUpdateMeta('og:image:height', '1200', 'property')
    setOrUpdateMeta('og:image:type', 'image/jpeg', 'property')
    setOrUpdateMeta('og:site_name', 'Maia e Tas', 'property')
    setOrUpdateMeta('twitter:card', 'summary_large_image')
    setOrUpdateMeta('twitter:image', `${siteUrl}/images/link-image-social.jpg`)
    setOrUpdateMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow')
    setCanonical(canonical)

    // Favicon
    setFaviconLink('icon', `/favicon-${faviconVariant}/favicon.ico`, { type: 'image/x-icon' })
    setFaviconLink('apple-touch-icon', `/favicon-${faviconVariant}/apple-touch-icon.png`)

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-page-meta-ld]')
    existingScripts.forEach((s) => s.remove())

    const items = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []
    items.forEach((item) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.pageMetaLd = 'true'
      script.textContent = JSON.stringify(item)
      document.head.appendChild(script)
    })

    return () => {
      document.querySelectorAll('script[data-page-meta-ld]').forEach((s) => s.remove())
    }
  }, [title, description, canonical, lang, jsonLd, noindex, faviconVariant])

  return null
}
